import cn from '@propertypal/shared/src/utils/cn';
import { useField, useFormikContext } from 'formik';
import React, { KeyboardEventHandler, useRef, useEffect, useState, FocusEventHandler, CSSProperties } from 'react';
import Select, { SelectInstance, OptionsOrGroups, InputActionMeta, components } from 'react-select';
import Async from 'react-select/async';
import { useTheme } from 'styled-components';
import StyledLabel from '../../_styles/StyledLabel';
import PropertyPalError from '../../error/PropertyPalError';
import FormTheme from '../../theme/FormTheme';
import colourStyles from './PropertyPalSelect.style';

export interface PropertyPalSelectProps {
  label?: string | JSX.Element;
  containerStyle?: CSSProperties;
  name: string;
  id?: string;
  options?: any[];
  loadOptions?:
    | ((inputValue: string, callback: (options: OptionsOrGroups<any, any>) => void) => void)
    | ((inputValue: string) => Promise<OptionsOrGroups<any, any>>);
  compo?: any;
  asyncSelect?: boolean;
  placeholder?: string;
  fixedHeight?: string;
  overRideFormTheme?: any;
  groupSelect?: boolean;
  isMulti?: boolean;
  scrollToError?: boolean;
  onMenuOpen?: () => void;
  onPPSelectChange?: (val: any, ref?: React.Ref<any>) => void;
  setInitRef?: (ini: React.Ref<any>) => void;
  onFocus?: (val: any, onChange: (value: any) => void) => void;
  noBorder?: boolean;
  isSearchable?: boolean;
  isClearable?: boolean;
  openMenuOnFocus?: boolean;
  err?: string;
  border?: boolean;
  className?: string;
  labelTestId?: string;
  labelId?: string;
  asyncTestId?: string;
  selectTestId?: string;
  testID?: string;
  tabIndex?: number;
  triggerOnChange?: Option;
  disabled?: boolean;
  errClassName?: string;
}

export interface Option {
  value?: any;
  label?: string;
}

export interface DropdownValue {
  type: string;
  value: string;
  label: string;
}

const Group = (props: any) => (
  <div>
    <components.Group {...props} />
  </div>
);

const PropertyPalSelect = (props: PropertyPalSelectProps) => {
  const {
    id,
    label,
    options,
    asyncSelect = false,
    loadOptions,
    compo,
    placeholder,
    fixedHeight,
    overRideFormTheme,
    groupSelect,
    isMulti,
    onPPSelectChange,
    noBorder,
    border,
    isSearchable,
    isClearable,
    err,
    labelTestId,
    tabIndex,
    selectTestId,
    testID,
    setInitRef,
    triggerOnChange,
    openMenuOnFocus,
    errClassName,
    ...rest
  } = props;

  const thme = useTheme();
  const { selectBox } = overRideFormTheme || FormTheme(thme);

  const asyncRef = useRef<SelectInstance<any> | null>(null);
  const [field, meta] = useField(props.name);
  const formikContext = useFormikContext();
  const [currentTyped, setCurrentTyped] = useState('');

  // Gets called when an option is chosen or created.
  const onChange = (value: any) => {
    setCurrentTyped(value?.label);
    formikContext.setFieldValue(field.name, value || '');
    if (props.onPPSelectChange) props.onPPSelectChange(value, asyncRef);
  };

  // Toggle menu by presence of input.
  // Reset value if input is emptied.
  const onInputChange = (value: any, context: InputActionMeta) => {
    if (context?.action === 'input-change') {
      setCurrentTyped(value || '');
    }
    if (context?.action === 'input-change' && value === '') {
      setCurrentTyped('');
      formikContext.setFieldValue(field.name, { value: '', label: '' });
    }

    return value;
  };

  // This is basically re-opening the menu and showing the correct version
  // after the menu has already been selected. handleInput change
  // fires off the async etc.

  // Restore input value.
  const onFocus: FocusEventHandler<HTMLInputElement> = async () => {
    if (asyncRef && asyncRef.current) {
      if (!props.onFocus) {
        const { onMenuOpen, onInputChange: inputChange } = asyncRef.current.props;
        await onMenuOpen();
        if (currentTyped) await inputChange(currentTyped, { action: 'set-value', prevInputValue: currentTyped });
      } else props.onFocus(field.value, onChange);
    } else if (props.onFocus) props.onFocus(field.value, onChange);
  };

  // Blur select element to trigger onFocus on next click.
  const onMenuClose = () => {
    if (asyncRef.current) asyncRef.current.blur();
  };

  // Close menu when pressing enter. Submit does not work on mobile.
  const onKeyDown: KeyboardEventHandler<HTMLDivElement> = (event) => {
    if (event.key === 'Enter') {
      onMenuClose();
    }
  };

  const getOptionValue = () => {
    const value =
      options &&
      options.find((option) => {
        return String(option.value) === String(field.value);
      });
    return value || '';
  };

  const getMultiOptionValues = (multiOptions: any) => {
    if (field && field.value && field.value.length > 0) {
      const values = field.value.map((v: any) => {
        return multiOptions.find((o: any) => {
          return (
            (o && o.value != null && v && v.value != null && o.value === v.value) ||
            (o && o.value != null && o.value === v)
          );
        });
      });
      return values;
    }
    return null;
  };

  const determineNonAsyncValue = () => {
    const defaultRet = '';

    if (options && !isMulti) {
      return getOptionValue();
    }
    if (isMulti) {
      return getMultiOptionValues(options);
    }
    return defaultRet;
  };

  const getError = () => {
    if (meta.error && typeof meta.error === 'object') return Object.values(meta.error).join(', ');

    return meta.error;
  };

  useEffect(() => {
    if (asyncRef && setInitRef) {
      setInitRef(asyncRef);
    }
  }, [asyncRef]);

  useEffect(() => {
    if (field.value && field.value.label && asyncRef && asyncRef.current && !currentTyped)
      setCurrentTyped(field.value.label);
  }, [field.value]);

  useEffect(() => {
    if (triggerOnChange) onChange(triggerOnChange);
  }, [triggerOnChange]);

  return (
    <div style={props.containerStyle} data-testid={testID}>
      {label && (
        <StyledLabel boldLabel id={props.labelId} data-testid={props.labelTestId} htmlFor={props.id || props.name}>
          {label}
        </StyledLabel>
      )}
      {asyncSelect ? (
        <>
          <Async
            classNamePrefix="pp-select"
            className={cn(meta.touched && meta.error != null && 'pp-text-input-error')}
            id={`${props.id}-container`}
            inputId={props.id}
            instanceId={props.id}
            data-testid={props.asyncTestId}
            {...field}
            {...rest}
            inputValue={currentTyped}
            tabIndex={tabIndex}
            ref={asyncRef}
            isClearable={isClearable}
            styles={colourStyles(meta, true, thme, fixedHeight, isMulti, noBorder, border, field.value, selectBox)}
            value={field.value}
            components={compo || null}
            loadOptions={loadOptions}
            placeholder={placeholder || 'Select...'}
            isMulti={isMulti}
            isSearchable
            openMenuOnFocus={openMenuOnFocus}
            defaultOptions={false}
            cacheOptions
            theme={(theme) => ({
              ...theme,
              colors: {
                ...theme.colors,
                primary: selectBox.option.selectedBackgroundColor /* selected option */,
                primary50: selectBox.option.selectedBackgroundColor /* click option */,
                primary25: selectBox.option.hoverBackgroundColor /* hover option */,
              },
            })}
            // Hook into events to make the editing work.
            onChange={onChange}
            onInputChange={onInputChange}
            onKeyDown={onKeyDown}
            onFocus={onFocus}
            onMenuClose={onMenuClose}
            aria-label={props.asyncTestId}
            aria-labelledby={props.labelId}
            isDisabled={props.disabled}
          />
        </>
      ) : (
        <Select
          classNamePrefix="pp-select"
          className={cn(meta.touched && meta.error != null && 'pp-text-input-error')}
          id={`${props.id}-container`}
          inputId={props.id}
          instanceId={props.id}
          {...field}
          {...rest}
          data-testid={props.selectTestId}
          tabIndex={tabIndex}
          value={determineNonAsyncValue()}
          isClearable={isClearable}
          styles={colourStyles(meta, false, thme, fixedHeight, isMulti, noBorder, border, undefined, selectBox)}
          components={groupSelect ? { IndicatorSeparator: null, Group } : { IndicatorSeparator: null }}
          options={options}
          placeholder={placeholder || 'Select...'}
          isMulti={isMulti}
          isSearchable={isSearchable}
          // // // Hook into events to make the editing work.
          theme={(theme) => ({
            ...theme,
            colors: {
              ...theme.colors,
              primary: selectBox.option.selectedBackgroundColor /* selected option */,
              primary50: selectBox.option.selectedBackgroundColor /* click option */,
              primary25: selectBox.option.hoverBackgroundColor /* hover option */,
            },
          })}
          onChange={(selectObj) => {
            // allow for 0 to be passed as field value
            let fieldValue = selectObj ? selectObj.value : undefined;
            if (isMulti) fieldValue = selectObj;

            // formik gets the full object.
            formikContext.setFieldValue(field.name, fieldValue);
            if (props.onPPSelectChange) props.onPPSelectChange(selectObj);
          }}
          onBlur={() => {}}
          onFocus={() => {}}
          isDisabled={props.disabled}
        />
      )}
      <PropertyPalError
        scrollToError={props.scrollToError}
        isVisible={meta.touched && meta.error != null}
        error={getError()}
        className={cn(meta.touched && meta.error != null && errClassName)}
      />
    </div>
  );
};

export default PropertyPalSelect;
